import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { bancosAPI, cotizacionesAPI, recibosCobroAPI } from '../services/api';
import { formatFechaSistema } from '../utils/fechasSistema';
import { aplicarBnaALinea, aplicarCambioMontoUsd, esDestinoUsd, pesosDesdeUsd, textoTasaUsada, usdDesdePesos } from '../utils/montoDolarFormaPago';

const esChequeTipoPropio = (nombre) => {
  const n = (nombre || '').toLowerCase();
  return n.includes('cheque propio') || n.includes('cheque de tercero');
};

const esFpBancariaSinCaja = (nombre) => {
  const n = (nombre || '').toLowerCase();
  return n.includes('transferencia') || esChequeTipoPropio(n);
};

/**
 * Editor de formas de pago.
 * - modoEgreso=false (default): ingresos (recibos) — cheque de tercero como alta (banco/nro).
 * - modoEgreso=true: egresos (gastos / similar a pago proveedor) — transferencia/cheque propio
 *   piden cuenta (nunca caja); cheque de tercero usa cartera.
 *
 * Props:
 *  - value / onChange / formasPago / bancos / setBancos / cuentasBancarias / cajas
 *  - fecha / fechaHoy / montoTotal / seccionTitulo
 *  - modoEgreso: boolean
 */
const FormasPagoEditor = ({
  value = [],
  onChange,
  formasPago = [],
  bancos = [],
  setBancos,
  cuentasBancarias = [],
  cajas = [],
  fecha,
  fechaHoy,
  montoTotal,
  seccionTitulo = 'Formas de Pago del Recibo *',
  modoEgreso = false,
}) => {
  const [modalCarteraOpen, setModalCarteraOpen] = useState(false);
  const [modalCarteraIndex, setModalCarteraIndex] = useState(null);
  const [carteraRecibidos, setCarteraRecibidos] = useState([]);
  const [carteraSeleccionados, setCarteraSeleccionados] = useState([]);
  const [carteraModalSortBy, setCarteraModalSortBy] = useState('fecha_cobro');
  const [carteraModalSortAsc, setCarteraModalSortAsc] = useState(true);
  const [cotizacionBna, setCotizacionBna] = useState(null);
  const [cotizacionManual, setCotizacionManual] = useState('');
  const [cotizacionError, setCotizacionError] = useState(false);

  const ctxDolar = { formasPago, cuentasBancarias, cajas, modoEgreso };
  const puntaCotizacion = modoEgreso ? 'compra' : 'venta';

  const cotizacionVigente = () => {
    if (cotizacionError) {
      const manual = parseFloat(String(cotizacionManual).replace(',', '.'));
      return manual > 0 ? manual : null;
    }
    const valor = Number(cotizacionBna?.[puntaCotizacion]);
    return valor > 0 ? valor : null;
  };

  const aplicarCotizacion = (lineas, cotiz) => {
    if (!(cotiz > 0)) return lineas;
    return lineas.map((fp) => {
      if (!esDestinoUsd(fp, ctxDolar)) return fp;
      if (fp.moneda_ingresada === 'AMBOS') return fp;
      if (fp.moneda_ingresada === 'USD' && fp.monto_usd !== '' && fp.monto_usd != null) {
        const pesos = pesosDesdeUsd(fp.monto_usd, cotiz);
        const monto = pesos == null ? fp.monto : String(pesos);
        const transferencia = (getFormaPagoNombre(fp.forma_pago_id).includes('transferencia'))
          ? { ...(fp.transferencia || {}), monto, fecha: fp.transferencia?.fecha || fecha }
          : fp.transferencia;
        return { ...fp, cotizacion: cotiz, monto, transferencia };
      }
      if (fp.monto !== '' && fp.monto != null) {
        const usd = usdDesdePesos(fp.monto, cotiz);
        return { ...fp, cotizacion: cotiz, monto_usd: usd == null ? fp.monto_usd : String(usd) };
      }
      return { ...fp, cotizacion: cotiz };
    });
  };

  const firmaDestinosUsd = value
    .map((fp) => `${fp.forma_pago_id || ''}:${fp.transferencia?.cuenta_bancaria_id || ''}:${fp.caja_id || ''}:${fp.cheque_propio?.cuenta_bancaria_id || ''}:${esDestinoUsd(fp, ctxDolar) ? 'USD' : 'ARS'}`)
    .join('|');
  const hayLineaUsd = firmaDestinosUsd.includes('USD');

  useEffect(() => {
    if (!hayLineaUsd || !fecha) return undefined;
    let cancelado = false;
    cotizacionesAPI.getDolarBna(fecha)
      .then((res) => {
        if (cancelado) return;
        if (res?.success && res.data) {
          setCotizacionBna(res.data);
          setCotizacionError(false);
        } else {
          setCotizacionError(true);
        }
      })
      .catch(() => {
        if (!cancelado) setCotizacionError(true);
      });
    return () => { cancelado = true; };
  }, [hayLineaUsd, fecha]);

  useEffect(() => {
    const cotiz = cotizacionError
      ? parseFloat(String(cotizacionManual).replace(',', '.'))
      : Number(cotizacionBna?.[puntaCotizacion]);
    if (!(cotiz > 0)) return;
    const next = aplicarCotizacion(value, cotiz);
    const igual = next.every((fp, i) => (
      String(fp.monto ?? '') === String(value[i].monto ?? '')
      && String(fp.monto_usd ?? '') === String(value[i].monto_usd ?? '')
      && Number(fp.cotizacion) === Number(value[i].cotizacion)
    ));
    if (!igual) onChange(next);
  }, [cotizacionBna, cotizacionManual, cotizacionError, puntaCotizacion, formasPago, cuentasBancarias, cajas]);

  const actualizarMontoUsd = (index, campo, raw) => {
    const cotiz = cotizacionVigente();
    const next = [...value];
    const actual = aplicarCambioMontoUsd(next[index], campo, raw, cotiz);
    const nombre = getFormaPagoNombre(actual.forma_pago_id);
    if (nombre.includes('transferencia')) {
      actual.transferencia = { ...(actual.transferencia || {}), monto: actual.monto, fecha: actual.transferencia?.fecha || fecha };
    }
    next[index] = actual;
    onChange(next);
  };

  const usarBnaEnLinea = (index) => {
    const cotiz = cotizacionVigente();
    if (!(cotiz > 0)) return;
    const next = [...value];
    const actual = aplicarBnaALinea(next[index], cotiz);
    const nombre = getFormaPagoNombre(actual.forma_pago_id);
    if (nombre.includes('transferencia')) {
      actual.transferencia = { ...(actual.transferencia || {}), monto: actual.monto, fecha: actual.transferencia?.fecha || fecha };
    }
    next[index] = actual;
    onChange(next);
  };

  const textoCotizacion = (fp) => {
    const puntaLabel = puntaCotizacion === 'compra' ? 'compra' : 'venta';
    let textoBna;
    if (cotizacionError) {
      textoBna = `No se pudo leer el BNA. Cargá la cotización de ${puntaLabel}.`;
    } else if (!cotizacionBna) {
      textoBna = 'Buscando dólar oficial BNA…';
    } else {
      const valor = Number(cotizacionBna[puntaCotizacion]);
      const aDma = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
      const fechaCot = cotizacionBna.fechaCotizacion;
      const fechaPedida = cotizacionBna.fecha;
      const cuando = fechaCot && fechaCot !== fechaPedida
        ? `del ${aDma(fechaCot)} (sin cotización el ${aDma(fechaPedida)})`
        : `del ${aDma(fechaCot || fechaPedida)}`;
      const fmt = valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
      textoBna = `BNA ${puntaLabel} ${cuando}: ${fmt}. El total del comprobante suma los pesos.`;
    }
    return textoTasaUsada(fp, {
      puntaLabel,
      textoBna,
      valorBna: cotizacionBna?.[puntaCotizacion],
    });
  };

  const getFormaPagoNombre = (formaPagoId) => {
    if (formaPagoId == null || formaPagoId === '') return '';
    const fp = formasPago.find((f) => String(f.id) === String(formaPagoId));
    return fp?.nombre?.toLowerCase() || '';
  };

  const abrirModalCartera = (index) => {
    const fp = value[index] || {};
    setModalCarteraIndex(index);
    setCarteraSeleccionados(
      fp.cheque_cartera_ids?.length
        ? [...fp.cheque_cartera_ids]
        : (fp.cheque_cartera_id ? [fp.cheque_cartera_id] : [])
    );
    setModalCarteraOpen(true);
    recibosCobroAPI.getCarteraCheques({ estado: 'recibido' }).then((res) => {
      setCarteraRecibidos(res.success && Array.isArray(res.data) ? res.data : []);
    }).catch(() => setCarteraRecibidos([]));
  };

  const confirmarModalCartera = () => {
    if (modalCarteraIndex == null || carteraSeleccionados.length === 0) {
      setModalCarteraOpen(false);
      setModalCarteraIndex(null);
      return;
    }
    const cheques = carteraRecibidos.filter((ch) => carteraSeleccionados.includes(ch.id));
    const total = cheques.reduce((s, ch) => s + parseFloat(ch.importe || 0), 0);
    const next = [...value];
    next[modalCarteraIndex] = {
      ...next[modalCarteraIndex],
      cheque_cartera_ids: carteraSeleccionados,
      cheque_cartera_id: carteraSeleccionados.length === 1 ? carteraSeleccionados[0] : undefined,
      monto: String(total.toFixed(2)),
    };
    onChange(next);
    setModalCarteraOpen(false);
    setModalCarteraIndex(null);
    setCarteraSeleccionados([]);
  };

  const toggleCarteraCheque = (id) => {
    setCarteraSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleCarteraModalSort = (colId) => {
    if (carteraModalSortBy === colId) setCarteraModalSortAsc((prev) => !prev);
    else {
      setCarteraModalSortBy(colId);
      setCarteraModalSortAsc(true);
    }
  };

  const carteraRecibidosOrdenados = useMemo(() => {
    if (!carteraRecibidos.length) return [];
    const col = carteraModalSortBy;
    const asc = carteraModalSortAsc;
    const getVal = (ch) => {
      if (col === 'numero') return (ch.numero || '').toString().toLowerCase();
      if (col === 'fecha_cobro') return ch.fecha_cobro ? new Date(ch.fecha_cobro).getTime() : 0;
      if (col === 'importe') return parseFloat(ch.importe || 0) || 0;
      if (col === 'cliente') return (ch.cliente_razon_social || '').toString().toLowerCase();
      if (col === 'banco') return (ch.cuenta_banco_nombre || '').toString().toLowerCase();
      return '';
    };
    return [...carteraRecibidos].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return asc ? -1 : 1;
      if (va > vb) return asc ? 1 : -1;
      return 0;
    });
  }, [carteraRecibidos, carteraModalSortBy, carteraModalSortAsc]);

  const agregar = () => {
    onChange([...value, { forma_pago_id: '', monto: '', cheque: {}, cheque_propio: {}, transferencia: {}, tarjeta: {} }]);
  };

  const eliminar = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const actualizarFormaPago = (index, field, val) => {
    const next = [...value];
    next[index] = { ...next[index], [field]: val };
    if (field === 'monto') {
      const nombreFp = getFormaPagoNombre(next[index].forma_pago_id);
      if (nombreFp.includes('transferencia')) {
        next[index].transferencia = {
          ...next[index].transferencia,
          monto: val,
          fecha: next[index].transferencia?.fecha || fecha,
        };
      }
    }
    if (field === 'forma_pago_id') {
      const formaPagoSel = formasPago.find((f) => String(f.id) === String(val));
      if (formaPagoSel?.nombre && esChequeTipoPropio(formaPagoSel.nombre)) {
        const hoy = fechaHoy ? fechaHoy() : '';
        next[index].cheque_propio = {
          ...next[index].cheque_propio,
          fecha_emision: next[index].cheque_propio?.fecha_emision || hoy,
          importe: next[index].cheque_propio?.importe ?? next[index].monto,
        };
      }
      if (formaPagoSel && (formaPagoSel.nombre || '').toLowerCase().includes('transferencia')) {
        const cuentaId = formaPagoSel.cuenta_bancaria_id != null ? formaPagoSel.cuenta_bancaria_id : null;
        next[index].transferencia = {
          ...next[index].transferencia,
          ...(cuentaId != null && { cuenta_bancaria_id: cuentaId }),
          fecha: next[index].transferencia?.fecha || fecha,
          monto: next[index].transferencia?.monto ?? next[index].monto,
        };
      }
    }
    onChange(next);
  };

  const actualizarDetalle = (index, tipo, field, val) => {
    const next = [...value];
    next[index] = { ...next[index], [tipo]: { ...(next[index][tipo] || {}), [field]: val } };
    onChange(next);
  };

  const totalFormas = value.reduce((sum, f) => sum + parseFloat(f.monto || 0), 0);
  const montoNum = parseFloat(montoTotal || 0);
  const coincide = Math.abs(totalFormas - montoNum) <= 0.01;

  return (
    <>
    <div className="form-group">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <label style={{ margin: 0 }}>{seccionTitulo}</label>
        <button type="button" onClick={agregar} className="btn-primary" style={{ padding: '6px 12px', fontSize: '13px' }}>
          + Agregar Forma de Pago
        </button>
      </div>

      {value.length === 0 && (
        <div style={{ padding: '15px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', marginBottom: '15px' }}>
          Debe agregar al menos una forma de pago. La suma de los montos debe ser igual al monto total.
        </div>
      )}

      {value.map((fp, index) => {
        const formaPagoNombre = getFormaPagoNombre(fp.forma_pago_id);
        const formaPagoObj = formasPago.find((f) => String(f.id) === String(fp.forma_pago_id));
        const transferenciaCuentaPrefijada = formaPagoObj?.cuenta_bancaria_id && (formaPagoObj?.nombre || '').toLowerCase().includes('transferencia');
        const showCaja = Boolean(
          formaPagoObj?.requiere_seleccion_fondo
          && cajas.length > 0
          && !esFpBancariaSinCaja(formaPagoNombre)
        );
        const esChequeTercero = formaPagoNombre.includes('cheque de tercero');
        return (
          <div key={index} style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '10px', background: '#f9f9f9' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px' }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: '140px', maxWidth: '160px' }}>
                <label style={{ fontSize: '12px' }}>Forma de pago *</label>
                <select
                  value={fp.forma_pago_id}
                  onChange={(e) => actualizarFormaPago(index, 'forma_pago_id', e.target.value)}
                  required
                  style={{ padding: '4px 6px', fontSize: '13px' }}
                >
                  <option value="">Seleccione...</option>
                  {formasPago.map((f) => (
                    <option key={f.id} value={f.id}>{f.nombre}</option>
                  ))}
                </select>
              </div>

              {esDestinoUsd(fp, ctxDolar) && !formaPagoNombre.includes('transferencia') && !(modoEgreso && esChequeTercero) ? (
                <>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: '110px', flex: '1 1 110px' }}>
                    <label htmlFor={`fp-pesos-${index}`} style={{ fontSize: '12px' }}>Pesos *</label>
                    <input
                      id={`fp-pesos-${index}`}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={fp.monto ?? ''}
                      onChange={(e) => actualizarMontoUsd(index, 'pesos', e.target.value)}
                      required
                      placeholder="0.00"
                      style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: '110px', flex: '1 1 110px' }}>
                    <label htmlFor={`fp-usd-${index}`} style={{ fontSize: '12px' }}>Dólares *</label>
                    <input
                      id={`fp-usd-${index}`}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={fp.monto_usd ?? ''}
                      onChange={(e) => actualizarMontoUsd(index, 'dolares', e.target.value)}
                      required
                      placeholder="0.00"
                      style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                    />
                  </div>
                </>
              ) : !formaPagoNombre.includes('transferencia') && !(modoEgreso && esChequeTercero) && (
                <div className="form-group" style={{ marginBottom: 0, minWidth: '75px', maxWidth: '95px' }}>
                  <label style={{ fontSize: '12px' }}>Monto *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={fp.monto ?? ''}
                    onChange={(e) => actualizarFormaPago(index, 'monto', e.target.value)}
                    required
                    placeholder="0.00"
                    style={{ padding: '4px 6px', fontSize: '13px' }}
                  />
                </div>
              )}

              {showCaja && (
                <div className="form-group" style={{ marginBottom: 0, minWidth: '140px' }}>
                  <label style={{ fontSize: '12px' }}>Caja *</label>
                  <select
                    value={fp.caja_id ?? ''}
                    onChange={(e) => actualizarFormaPago(index, 'caja_id', e.target.value)}
                    required
                    style={{ padding: '4px 6px', fontSize: '13px' }}
                  >
                    <option value="">Seleccione caja...</option>
                    {cajas.map((c) => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              {esChequeTipoPropio(formaPagoNombre) && !(modoEgreso && esChequeTercero) && (
                <>
                  {esChequeTercero ? (
                    <div className="form-group" style={{ minWidth: '130px', marginBottom: 0 }}>
                      <label style={{ fontSize: '12px' }}>Banco *</label>
                      <select
                        value={fp.cheque_propio?.banco_id ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__agregar_banco__') {
                            const nombre = window.prompt('Nombre del nuevo banco:');
                            if (nombre && nombre.trim()) {
                              bancosAPI.create({ nombre: nombre.trim() })
                                .then((res) => {
                                  if (res.success && res.data?.id) {
                                    if (setBancos) setBancos((prev) => [...prev, { id: res.data.id, nombre: nombre.trim() }]);
                                    actualizarDetalle(index, 'cheque_propio', 'banco_id', res.data.id);
                                  }
                                })
                                .catch(() => toast.error('No se pudo crear el banco'));
                            }
                            return;
                          }
                          actualizarDetalle(index, 'cheque_propio', 'banco_id', v);
                        }}
                        required
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                      >
                        <option value="">Banco...</option>
                        {bancos.map((b) => (
                          <option key={b.id} value={b.id}>{b.nombre}</option>
                        ))}
                        <option value="__agregar_banco__">+ Agregar banco...</option>
                      </select>
                    </div>
                  ) : (
                    <div className="form-group" style={{ minWidth: '120px', marginBottom: 0 }}>
                      <label style={{ fontSize: '12px' }}>Cuenta *</label>
                      <select
                        value={fp.cheque_propio?.cuenta_bancaria_id ?? ''}
                        onChange={(e) => actualizarDetalle(index, 'cheque_propio', 'cuenta_bancaria_id', e.target.value)}
                        required
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                      >
                        <option value="">Cuenta...</option>
                        {cuentasBancarias.map((cb) => (
                          <option key={cb.id} value={cb.id}>{cb.banco_nombre || 'Banco'} - {cb.numero_cuenta}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group" style={{ minWidth: '55px', marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>Nro *</label>
                    <input
                      type="text"
                      value={fp.cheque_propio?.numero || ''}
                      onChange={(e) => actualizarDetalle(index, 'cheque_propio', 'numero', e.target.value)}
                      required
                      placeholder="Nº"
                      style={{ padding: '4px 6px', fontSize: '13px', width: '60px' }}
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: '95px', marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>F. emisión *</label>
                    <input
                      type="date"
                      value={fp.cheque_propio?.fecha_emision || (fechaHoy ? fechaHoy() : '')}
                      onChange={(e) => actualizarDetalle(index, 'cheque_propio', 'fecha_emision', e.target.value)}
                      required
                      style={{ padding: '4px 6px', fontSize: '13px' }}
                    />
                  </div>
                  <div className="form-group" style={{ minWidth: '95px', marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>F. cobro</label>
                    <input
                      type="date"
                      value={fp.cheque_propio?.fecha_cobro || ''}
                      onChange={(e) => actualizarDetalle(index, 'cheque_propio', 'fecha_cobro', e.target.value)}
                      min={fp.cheque_propio?.fecha_emision || undefined}
                      style={{ padding: '4px 6px', fontSize: '13px' }}
                    />
                  </div>
                  {esChequeTercero && !modoEgreso && (
                    <div className="form-group" style={{ minWidth: '140px', marginBottom: 0 }}>
                      <label style={{ fontSize: '12px' }}>Titular / librador</label>
                      <input
                        type="text"
                        value={fp.cheque_propio?.titular || ''}
                        onChange={(e) => actualizarDetalle(index, 'cheque_propio', 'titular', e.target.value)}
                        placeholder="Quién firma el cheque"
                        style={{ padding: '4px 6px', fontSize: '13px', minWidth: '140px' }}
                      />
                    </div>
                  )}
                  <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
                    <label className="form-toggle-wrap" style={{ marginBottom: 0, fontSize: '12px' }}>
                      <span className="toggle-switch-box">
                        <input
                          type="checkbox"
                          checked={Boolean(fp.cheque_propio?.es_electronico)}
                          onChange={(e) => actualizarDetalle(index, 'cheque_propio', 'es_electronico', e.target.checked)}
                        />
                        <span className="form-toggle-switch" />
                      </span>
                      <span className="form-toggle-label">Electrónico</span>
                    </label>
                  </div>
                </>
              )}

              {modoEgreso && esChequeTercero && (
                <div className="form-group" style={{ minWidth: '180px', marginBottom: 0 }}>
                  <label style={{ fontSize: '12px' }}>Cartera *</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                      onClick={() => abrirModalCartera(index)}
                    >
                      {fp.cheque_cartera_ids?.length || fp.cheque_cartera_id ? 'Cambiar' : 'Seleccionar cheques'}
                    </button>
                    {(fp.cheque_cartera_ids?.length || fp.cheque_cartera_id) ? (
                      <span style={{ fontSize: '12px', color: '#333' }}>
                        {fp.cheque_cartera_ids?.length || 1} cheque(s) · ${parseFloat(fp.monto || 0).toFixed(2)}
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#856404' }}>Sin cheques</span>
                    )}
                  </div>
                </div>
              )}

              {formaPagoNombre.includes('transferencia') && (
                <>
                  {transferenciaCuentaPrefijada ? (
                    <span style={{ fontSize: '12px', alignSelf: 'center', color: '#555', marginRight: '8px' }}>
                      Cuenta: {cuentasBancarias.find((cb) => String(cb.id) === String(formaPagoObj?.cuenta_bancaria_id))?.numero_cuenta || '—'}
                    </span>
                  ) : (
                    <div className="form-group" style={{ minWidth: '130px', marginBottom: 0 }}>
                      <label style={{ fontSize: '12px' }}>Cuenta *</label>
                      <select
                        value={fp.transferencia?.cuenta_bancaria_id || ''}
                        onChange={(e) => actualizarDetalle(index, 'transferencia', 'cuenta_bancaria_id', e.target.value)}
                        required
                        style={{ padding: '4px 6px', fontSize: '13px' }}
                      >
                        <option value="">Seleccione...</option>
                        {cuentasBancarias.map((cb) => (
                          <option key={cb.id} value={cb.id}>{cb.numero_cuenta} - {cb.banco_nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group" style={{ minWidth: '95px', marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>Fecha *</label>
                    <input
                      type="date"
                      value={fp.transferencia?.fecha || fecha}
                      onChange={(e) => actualizarDetalle(index, 'transferencia', 'fecha', e.target.value)}
                      required
                      style={{ padding: '4px 6px', fontSize: '13px' }}
                    />
                  </div>
                  {esDestinoUsd(fp, ctxDolar) ? (
                    <>
                      <div className="form-group" style={{ minWidth: '110px', marginBottom: 0, flex: '1 1 110px' }}>
                        <label htmlFor={`tr-pesos-${index}`} style={{ fontSize: '12px' }}>Pesos *</label>
                        <input
                          id={`tr-pesos-${index}`}
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={fp.monto ?? ''}
                          onChange={(e) => actualizarMontoUsd(index, 'pesos', e.target.value)}
                          required
                          style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                        />
                      </div>
                      <div className="form-group" style={{ minWidth: '110px', marginBottom: 0, flex: '1 1 110px' }}>
                        <label htmlFor={`tr-usd-${index}`} style={{ fontSize: '12px' }}>Dólares *</label>
                        <input
                          id={`tr-usd-${index}`}
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={fp.monto_usd ?? ''}
                          onChange={(e) => actualizarMontoUsd(index, 'dolares', e.target.value)}
                          required
                          style={{ padding: '8px', fontSize: '16px', minHeight: '44px' }}
                        />
                      </div>
                    </>
                  ) : (
                  <div className="form-group" style={{ minWidth: '75px', marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>Monto *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={fp.transferencia?.monto ?? fp.monto ?? ''}
                      onChange={(e) => {
                        actualizarDetalle(index, 'transferencia', 'monto', e.target.value);
                        actualizarFormaPago(index, 'monto', e.target.value);
                      }}
                      required
                      style={{ padding: '4px 6px', fontSize: '13px' }}
                    />
                  </div>
                  )}
                </>
              )}

              {formaPagoNombre.includes('tarjeta') && (
                <div className="form-group" style={{ minWidth: '120px', marginBottom: 0 }}>
                  <label style={{ fontSize: '12px' }}>Nº ticket *</label>
                  <input
                    type="text"
                    value={fp.tarjeta?.numero_ticket || ''}
                    onChange={(e) => actualizarDetalle(index, 'tarjeta', 'numero_ticket', e.target.value)}
                    required
                    placeholder="Ticket"
                    style={{ padding: '4px 6px', fontSize: '13px' }}
                  />
                </div>
              )}

              <button type="button" onClick={() => eliminar(index)} className="btn-delete" style={{ padding: '4px 8px', fontSize: '12px', marginLeft: 'auto' }}>
                Eliminar
              </button>
            </div>
            {esDestinoUsd(fp, ctxDolar) && (
              <div style={{ marginTop: '8px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#1a1a1a' }}>{textoCotizacion(fp)}</p>
                {fp.moneda_ingresada === 'AMBOS' && (
                  <button
                    type="button"
                    onClick={() => usarBnaEnLinea(index)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginTop: '6px',
                      padding: '10px 0',
                      fontSize: '16px',
                      minHeight: '44px',
                      background: 'none',
                      border: 'none',
                      color: '#1565c0',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Usar BNA
                  </button>
                )}
                {cotizacionError && (
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.0001"
                    min="0"
                    aria-label={puntaCotizacion === 'compra' ? 'Cotización BNA compra' : 'Cotización BNA venta'}
                    value={cotizacionManual}
                    onChange={(e) => setCotizacionManual(e.target.value)}
                    placeholder={puntaCotizacion === 'compra' ? 'Compra' : 'Venta'}
                    style={{ marginTop: '6px', padding: '8px', fontSize: '16px', minHeight: '44px', maxWidth: '160px' }}
                  />
                )}
              </div>
            )}

            {formaPagoNombre.includes('cheque') && !esChequeTipoPropio(formaPagoNombre) && (
              <div style={{ marginTop: '15px', padding: '10px', background: 'white', borderRadius: '4px' }}>
                <strong style={{ display: 'block', marginBottom: '10px' }}>Datos del Cheque:</strong>
                <div className="form-row">
                  <div className="form-group">
                    <label>Banco *</label>
                    <select value={fp.cheque?.banco_id || ''} onChange={(e) => actualizarDetalle(index, 'cheque', 'banco_id', e.target.value)} required>
                      <option value="">Seleccione...</option>
                      {bancos.map((b) => (
                        <option key={b.id} value={b.id}>{b.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Número de Cheque *</label>
                    <input type="text" value={fp.cheque?.numero_cheque || ''} onChange={(e) => actualizarDetalle(index, 'cheque', 'numero_cheque', e.target.value)} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Importe *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={fp.cheque?.importe || fp.monto || ''}
                      onChange={(e) => {
                        actualizarDetalle(index, 'cheque', 'importe', e.target.value);
                        actualizarFormaPago(index, 'monto', e.target.value);
                      }}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Fecha de Emisión *</label>
                    <input type="date" value={fp.cheque?.fecha_emision || ''} onChange={(e) => actualizarDetalle(index, 'cheque', 'fecha_emision', e.target.value)} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Fecha de Cobro</label>
                    <input type="date" value={fp.cheque?.fecha_cobro || ''} onChange={(e) => actualizarDetalle(index, 'cheque', 'fecha_cobro', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Titular *</label>
                    <input type="text" value={fp.cheque?.titular || ''} onChange={(e) => actualizarDetalle(index, 'cheque', 'titular', e.target.value)} required />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {value.length > 0 && (
        <div
          style={{
            padding: '10px',
            background: coincide ? '#d4edda' : '#f8d7da',
            border: `1px solid ${coincide ? '#28a745' : '#dc3545'}`,
            borderRadius: '4px',
            marginTop: '10px',
          }}
        >
          <strong>Total formas de pago: ${totalFormas.toFixed(2)}</strong>
          {' · '}
          <span>Monto total: ${montoNum.toFixed(2)}</span>
          {(montoNum - totalFormas) > 0.01 && (
            <>
              {' · '}
              <strong style={{ color: '#856404' }}>Faltante: ${(montoNum - totalFormas).toFixed(2)}</strong>
            </>
          )}
          {!coincide && (
            <div style={{ color: '#dc3545', marginTop: '5px', fontWeight: 'bold' }}>
              La suma debe ser igual al monto total
            </div>
          )}
        </div>
      )}
    </div>

    {modalCarteraOpen && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
        onClick={() => { setModalCarteraOpen(false); setModalCarteraIndex(null); }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Cartera de cheques (estado: recibido)</h3>
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
            Seleccione uno o más cheques. El monto de la forma de pago será la suma de los importes seleccionados.
          </p>
          <div style={{ maxHeight: '320px', overflowY: 'auto', marginBottom: '16px', border: '1px solid #ddd', borderRadius: '6px' }}>
            {carteraRecibidos.length === 0 ? (
              <div style={{ padding: '20px', color: '#666' }}>No hay cheques en estado recibido.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}></th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleCarteraModalSort('numero')}>
                      Nº {carteraModalSortBy === 'numero' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleCarteraModalSort('fecha_cobro')}>
                      F. cobro {carteraModalSortBy === 'fecha_cobro' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleCarteraModalSort('importe')}>
                      Importe {carteraModalSortBy === 'importe' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleCarteraModalSort('cliente')}>
                      Cliente {carteraModalSortBy === 'cliente' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleCarteraModalSort('banco')}>
                      Banco {carteraModalSortBy === 'banco' ? (carteraModalSortAsc ? '▲' : '▼') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {carteraRecibidosOrdenados.map((ch) => (
                    <tr key={ch.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>
                        <input
                          type="checkbox"
                          checked={carteraSeleccionados.includes(ch.id)}
                          onChange={() => toggleCarteraCheque(ch.id)}
                        />
                      </td>
                      <td style={{ padding: '8px' }}>{ch.numero || '-'}</td>
                      <td style={{ padding: '8px' }}>{ch.fecha_cobro ? formatFechaSistema(ch.fecha_cobro) : '-'}</td>
                      <td style={{ padding: '8px' }}>${parseFloat(ch.importe || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '8px' }}>{ch.cliente_razon_social || '-'}</td>
                      <td style={{ padding: '8px' }}>{ch.cuenta_banco_nombre || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" className="btn-secondary" onClick={() => { setModalCarteraOpen(false); setModalCarteraIndex(null); }}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={confirmarModalCartera}
              disabled={carteraSeleccionados.length === 0}
            >
              Confirmar ({carteraSeleccionados.length} seleccionado(s) - $
              {carteraRecibidos
                .filter((ch) => carteraSeleccionados.includes(ch.id))
                .reduce((s, ch) => s + parseFloat(ch.importe || 0), 0)
                .toFixed(2)})
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default FormasPagoEditor;
